/* Domovoi · notifications bell (window.DVNotify)

   One bell, in the top bar of every page, for all three products: what your
   partner did in Catered, Curated and Considered since you last looked.

   Data: RPC my_notifications (Domovoi DB). The database writes the log with
   triggers, groups it per person / product / kind / day, and never returns
   anything about a gift for you. Opening the bell marks it all seen
   (mark_notifications_seen).

   No SDK here, same as account-menu.js: one fetch with the stored token.
   Bundled into /assets/site.js; account-menu.js starts it once someone is
   signed in, by calling DVNotify.attach(el) for each avatar it draws. */
import { API_URL, PUBLISHABLE_KEY } from '../core/config.js';
import { inShell } from '../core/router.js';

(function () {
  if (window.DVNotify) return;

  var API = API_URL;
  var KEY = PUBLISHABLE_KEY;
  var CACHE = 'domovoi.notify.v1';
  var POLL_MS = 90 * 1000;
  var OPEN = 'dv-bell-open';

  var state = { data: null, loading: false, at: 0 };
  var bells = [];

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function svg(p, size) {
    return '<svg width="' + (size || 17) + '" height="' + (size || 17) + '" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + p + '</svg>';
  }
  var ICON_BELL = svg('<path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>');
  var PRODUCT = {
    catered:    { label: 'Catered',    tint: 'butter', href: '/catered/' },
    curated:    { label: 'Curated',    tint: 'sky',    href: '/curated/' },
    considered: { label: 'Considered', tint: 'coral',  href: '/considered/calendar' },
    courted:    { label: 'Courted',    tint: 'pistachio', href: '/courted/ideas' },
    week:       { label: 'Your week',  tint: 'pop',    href: '/' },
  };

  /* ---- who's who ---- */
  function me() { return window.DVIdentity && window.DVIdentity.current(); }
  function partnerName() { var m = me(); return (m && m.partner) || 'Your partner'; }
  function myId() { var m = me(); return m && m.id; }

  // Family words read differently from the other side: your "Mum" is Olya's
  // "Ben's mum". Anything else is a name and stays as it is.
  var FAMILY = /^(mum|mom|mother|mam|dad|father|nan|nana|nanny|gran|granny|grandma|grandmother|grandad|granddad|grandpa|grandfather|auntie|aunt|uncle|brother|sister|step-?mum|step-?mom|step-?dad|cousin|son|daughter)$/i;
  function personLabel(ref) {
    if (!ref || !ref.name) return 'someone';
    var mine = ref.side && ref.side === myId();
    if (!mine && ref.side && FAMILY.test(String(ref.name).trim())) {
      return partnerName() + '’s ' + String(ref.name).trim().toLowerCase();
    }
    return ref.name;
  }
  var OCC = { birthday: 'birthday', christmas: 'Christmas', valentines: 'Valentine’s', 'mothers-day': 'Mother’s Day', 'fathers-day': 'Father’s Day', anniversary: 'anniversary', easter: 'Easter', hanukkah: 'Hanukkah', diwali: 'Diwali', 'eid-al-fitr': 'Eid', 'lunar-new-year': 'Lunar New Year' };
  function occasionLabel(ref) {
    if (!ref || !ref.occasion) return '';
    if (/^event:/.test(ref.occasion)) return '';
    var o = OCC[ref.occasion] || ref.occasion;
    return o === 'birthday' ? personLabel(ref) + '’s birthday' : o;
  }
  function list(subjects, n) {
    var s = (subjects || []).filter(Boolean).map(function (x) { return '<b>' + esc(x) + '</b>'; });
    if (n > s.length && s.length) return s.join(', ') + ' and ' + (n - s.length) + ' more';
    if (s.length <= 1) return s.join('');
    return s.slice(0, -1).join(', ') + ' and ' + s[s.length - 1];
  }

  /* A perfect day put on the week (0076): "planned a Toronto evening for Saturday: Dinner out, a comedy show, then
     drinks out" (the note's words are ours, never Google's names). */
  function perfectPlanned(r) {
    var t = String(r.title || ''), when = r.date === todayIso() ? 'today' : dayName(r.date);
    if (/^(A|An|The|Our) /.test(t)) t = t.charAt(0).toLowerCase() + t.slice(1);
    return 'planned <b>' + esc(t) + '</b> ' + (Number(r.days) === 3 ? 'from ' : 'for ') + esc(when) + (r.things ? ': ' + esc(r.things) : '');
  }

  /* ---- one line of text per grouped item ---- */
  function sentence(it) {
    var n = it.n || 1, s = it.subjects || [], one = '<b>' + esc(s[0] || '') + '</b>', r = it.ref || {};
    var who = '<b>' + esc(personLabel(r)) + '</b>';
    switch (it.product + ':' + it.kind) {
      case 'catered:recipe_saved':  return n > 1 ? 'saved ' + n + ' recipes: ' + list(s, n) : 'saved ' + one;
      case 'catered:planned':       return n > 1 ? 'planned ' + n + ' dinners: ' + list(s, n) : 'planned ' + one + (r.date ? ' for ' + dayName(r.date) : '');
      case 'catered:moved':         return n > 1 ? 'moved ' + n + ' dinners around' : 'moved ' + one + (r.date ? ' to ' + dayName(r.date) : '');
      case 'catered:list_added':    return 'added ' + list(s, n) + ' to the shopping list';
      case 'catered:recipe_matched': return n > 1 ? 'wants to cook ' + n + ' recipes you said yes to: ' + list(s, n) + '. They’re in your recipes' : 'wants to cook ' + one + ' too. It’s in your recipes';
      case 'curated:said_yes':      return n > 1 ? 'said yes to ' + n + ' titles. Your turn to swipe' : 'said yes to ' + one + '. Your turn';
      case 'curated:matched':       return n > 1 ? 'matched with you on ' + n + ' titles: ' + list(s, n) : 'matched with you on ' + one;
      case 'curated:seen_it':       return n > 1 ? 'has already watched ' + n + ' titles: ' + list(s, n) + '. Fancy any of them?' : 'has already watched ' + one + '. Fancy it?';
      case 'curated:watching':      return 'started watching ' + list(s, n);
      case 'curated:list_shared':   return 'made your watchlist shareable. You can turn it off under Watchlist → Share';
      case 'curated:watched':       return 'finished ' + list(s, n);
      case 'curated:dropped':       return 'stopped watching ' + list(s, n);
      case 'curated:watch_planned': return 'is up for ' + one + (r.date ? (r.date === todayIso() ? ' tonight' : ' on ' + dayName(r.date)) : '');
      case 'week:plan_added':       return 'added ' + one + ' for you both' + (r.date ? (r.date === todayIso() ? ', today' : ', ' + dayName(r.date)) : '');
      // the games (0061)
      case 'week:would_done':       return 'guessed what you’d say yes to' + (r.of ? ', and got ' + esc(String(r.right || 0)) + ' of ' + esc(String(r.of)) + ' right. Your turn' : '. Your turn');
      case 'week:evening_built':    return 'built a perfect evening for ' + (r.date === todayIso() ? 'tonight' : dayName(r.date)) + '. Build yours to see them side by side';
      case 'week:evening_both':     return 'built a perfect evening for ' + (r.date === todayIso() ? 'tonight' : dayName(r.date)) + ' too. See them side by side';
      case 'week:evening_planned':  return r.perfect && r.title ? perfectPlanned(r) : 'planned ' + (r.date === todayIso() ? (r.perfect ? 'today' : 'tonight') : dayName(r.date)) + ': ' + one;
      // Perfect days (0073): something added to one ("Walk along the Seine to A weekend in Paris")
      case 'week:perfect_added':    return 'added ' + (n > 1 ? list(s, n) : one) + ' to ' + (r.title ? '<b>' + esc(r.title) + '</b>' : 'a perfect day');
      case 'curated:watch_agreed':  return 'is up for ' + one + (r.date ? (r.date === todayIso() ? ' tonight' : ' on ' + dayName(r.date)) : '') + ' too';
      case 'curated:new_season':    return n > 1 ? 'New seasons of shows you’ve watched: ' + list(s, n) + '. Add them to Up next?'
                                     : one + ' season ' + esc(r.season || '') + (r.out ? ' is out' : r.date ? ' starts ' + shortDay(r.date) : ' is coming') + '. Add it to Up next?';
      case 'courted:date_proposed': return n > 1 ? 'is up for ' + n + ' dates. Are you in?' : 'is up for ' + '<b>' + esc(dateWhat(r)(s[0] || 'a date')) + '</b>' + dateWhen(r) + '. Are you in?';
      case 'courted:date_agreed':   return n > 1 ? 'is in for ' + n + ' dates. They’re on your week' : 'is in for ' + '<b>' + esc(dateWhat(r)(s[0] || 'a date')) + '</b>' + dateWhen(r) + '. It’s a date';
      case 'courted:date_matched':  return n > 1 ? 'said yes to ' + n + ' places you like too. They’re in your date ideas' : 'said yes to a place you like too. It’s in your date ideas';
      case 'considered:person_added':    return 'added ' + who + ' to your people';
      case 'considered:people_imported': return 'imported ' + n + ' people from a calendar';
      case 'considered:person_updated':  return 'updated ' + who;
      case 'considered:person_removed':  return 'removed ' + who + ' from your people';
      case 'considered:gift_added':      return (n > 1 ? 'added ' + n + ' gift ideas for ' : 'added a gift idea for ') + who + ': ' + list(s, n);
      case 'considered:gift_confirmed':  return 'chose ' + list(s, n) + ' for ' + (occasionLabel(r) && r.occasion !== 'birthday' ? who + ' at ' + esc(occasionLabel(r)) : esc(occasionLabel(r) || personLabel(r)));
      case 'considered:gift_bought':     return 'bought ' + list(s, n) + ' for ' + (occasionLabel(r) && r.occasion !== 'birthday' ? who + ' at ' + esc(occasionLabel(r)) : esc(occasionLabel(r) || personLabel(r)));
      case 'considered:gift_unconfirmed':return 'moved ' + list(s, n) + ' back to ideas';
      case 'considered:brand_added':     return 'is considering ' + list(s, n) + ' for ' + who;
      case 'considered:gift_edited':     return 'updated ' + list(s, n) + ' for ' + who;
      case 'considered:gift_removed':    return 'removed ' + list(s, n) + ' for ' + who;
      case 'considered:event_added':     return 'added ' + one + ' to the calendar';
      default: return esc(it.kind.replace(/_/g, ' ')) + (s[0] ? ' ' + one : '');
    }
  }
  // a date's name mid-sentence: a place's name as it is, a kind ("Dinner out") in lower case
  function dateWhat(r) { return function (t) { t = String(t || ''); return r && r.named ? t : t.charAt(0).toLowerCase() + t.slice(1); }; }
  function dateWhen(r) {
    if (!r || !r.date) return '';
    var d = r.date === todayIso() ? (r.evening === false ? ' today' : ' tonight') : ' on ' + dayName(r.date);
    return d + (r.at ? ' at ' + esc(r.at) : '');
  }
  function todayIso() { var d = new Date(); return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0'); }
  function shortDay(iso) {
    var d = new Date(iso + 'T12:00:00');
    return isNaN(d) ? '' : d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  }
  function dayName(iso) {
    var d = new Date(iso + 'T12:00:00');
    return isNaN(d) ? '' : d.toLocaleDateString(undefined, { weekday: 'long' });
  }
  function link(it) {
    var r = it.ref || {};
    if (it.product === 'curated' && r.tmdb_id && r.media_type) return '/curated/?t=' + encodeURIComponent(r.media_type + '-' + r.tmdb_id);
    if (it.product === 'courted' && /^date_(proposed|agreed)$/.test(it.kind)) return '/courted/';
    if (it.product === 'week' && it.kind === 'perfect_added' && r.day) return '/courted/day/' + encodeURIComponent(r.day);
    if (it.product === 'week' && it.kind === 'evening_planned' && r.perfect) return '/courted/day/' + encodeURIComponent(r.perfect);
    if (it.product === 'considered' && r.person_id && /^(gift_|brand_)/.test(it.kind)) return '/considered/person/' + encodeURIComponent(r.person_id);
    return PRODUCT[it.product] ? PRODUCT[it.product].href : '/';
  }
  function ago(iso) {
    var s = (Date.now() - new Date(iso).getTime()) / 1000;
    if (s < 90) return 'Just now';
    if (s < 3570) return Math.round(s / 60) + 'm ago';
    if (s < 86400) return Math.round(s / 3600) + 'h ago';
    if (s < 172800) return 'Yesterday';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }

  /* ---- data ---- */
  function readCache() { try { return JSON.parse(localStorage.getItem(CACHE) || 'null'); } catch (e) { return null; } }
  function writeCache(d) { try { localStorage.setItem(CACHE, JSON.stringify({ id: myId(), data: d })); } catch (e) {} }

  function rpc(name, body) {
    var s = window.DVIdentity && window.DVIdentity.live();
    if (!s) return Promise.reject(new Error('no live token'));
    return fetch(API + '/rest/v1/rpc/' + name, {
      method: 'POST',
      headers: { apikey: KEY, Authorization: 'Bearer ' + s.access_token, 'Content-Type': 'application/json' },
      body: JSON.stringify(body || {}),
    }).then(function (r) {
      if (!r.ok) throw new Error(name + ' ' + r.status);
      return r.status === 204 ? null : r.json();
    });
  }

  function load(force) {
    if (state.loading) return;
    if (!force && Date.now() - state.at < 20000) return;
    state.loading = true;
    var tz = 'America/Toronto';
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || tz; } catch (e) {}
    rpc('my_notifications', { p_tz: tz, p_limit: 30 }).then(function (d) {
      state.data = d || { unread: 0, items: [] };
      state.at = Date.now();
      writeCache(state.data);
      draw();
    }).catch(function () { /* token not fresh yet: keep what we have */ })
      .then(function () { state.loading = false; });
  }

  function markSeen() {
    if (!state.data || !state.data.unread) return Promise.resolve();
    return rpc('mark_notifications_seen').then(function () {
      state.data.unread = 0;
      (state.data.items || []).forEach(function (i) { i.unread = false; });
      writeCache(state.data);
      bells.forEach(function (b) { paintCount(b); });   // keep the open panel's highlights until it closes
    }).catch(function () {});
  }

  /* ---- drawing ---- */
  function paintCount(b) {
    var n = state.data ? state.data.unread : 0;
    b.count.textContent = n > 9 ? '9+' : String(n || '');
    b.count.style.display = n ? '' : 'none';
    b.btn.setAttribute('aria-label', n ? n + ' new from ' + partnerName() : 'Notifications');
    b.btn.classList.toggle('has', !!n);
  }
  function paintPanel(b) {
    if (rd()) return paintPanelR(b);
    var d = state.data, items = (d && d.items) || [];
    var head = '<div class="dv-bell-head"><span>What’s new</span>' +
      (d && d.unread ? '<i>' + d.unread + ' new</i>' : '') + '</div>';
    if (!d) { b.panel.innerHTML = head + '<p class="dv-bell-empty">Loading…</p>'; return; }
    if (!items.length) {
      b.panel.innerHTML = head + '<p class="dv-bell-empty">Nothing new. When ' + esc(partnerName()) +
        ' plans a dinner, says yes to a show or adds a gift idea, it’ll show up here.</p>';
      return;
    }
    var name = esc(partnerName());
    b.panel.innerHTML = head + '<div class="dv-bell-list">' + items.map(function (it) {
      var p = PRODUCT[it.product] || { label: it.product, tint: 'milk' };
      var thumb = it.ref && it.ref.poster
        ? '<img class="dv-bell-poster" alt="" src="https://image.tmdb.org/t/p/w92' + esc(it.ref.poster) + '">'
        : '<span class="dv-bell-orb ' + p.tint + '">' + esc(name.charAt(0)) + '</span>';
      return '<a class="dv-bell-item' + (it.unread ? ' unread' : '') + '" href="' + esc(link(it)) + '">' + thumb +
        '<span class="dv-bell-text">' + (it.actor ? '<b>' + name + '</b> ' : '') + sentence(it) +
        '<small><span class="dv-bell-tag ' + p.tint + '">' + p.label + '</span>' + esc(ago(it.at)) + '</small></span></a>';
    }).join('') + '</div>';
  }
  /* ---- the redesign's Notifications (the canvas's Notifications board) ----
     A page of its own on a phone and in the app (back, the big title, the days, Notification settings in the app);
     a card under the bell on a computer. Each row: whose it is (your partner's orb with the product on it, or both
     of you), the sentence, when, the poster, and one thing to do where there's an obvious one. */
  var rd = function () { return document.documentElement.hasAttribute('data-redesign'); };
  var PATH = {
    catered: '<path d="M7.5 3v6.2M4.8 3v4.6a2.7 2.7 0 0 0 5.4 0V3M7.5 9.8V21"/><path d="M17.8 3c-2.4 2.4-3.3 6.4-1.9 10.2h1.9V21"/>',
    curated: '<rect x="2.6" y="4.4" width="18.8" height="12.6" rx="3"/><path d="M8 20.6h8"/>',
    courted: '<path d="M12 21s-6.5-6.2-6.5-11a6.5 6.5 0 0 1 13 0c0 4.8-6.5 11-6.5 11Z"/>',
    considered: '<rect x="3.4" y="8" width="17.2" height="4.2" rx="1.2"/><path d="M5 12.2v7.3a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5v-7.3M12 8v13"/>',
    week: '<rect x="3.5" y="5" width="17" height="15.5" rx="3"/><path d="M3.5 10h17M8 3v4M16 3v4"/>',
  };
  function dayStart(d) { d = new Date(d); d.setHours(0, 0, 0, 0); return d.getTime(); }
  function groupOf(iso) {
    var days = Math.round((dayStart(Date.now()) - dayStart(iso)) / 86400000);
    return days <= 0 ? 'Today' : days === 1 ? 'Yesterday' : days < 7 ? 'Earlier this week' : 'Earlier';
  }
  function whenR(iso, g) {
    var d = new Date(iso);
    if (g === 'Today' || g === 'Yesterday') return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s?([AP])M$/i, function (m, x) { return x.toLowerCase() + 'm'; });
    if (g === 'Earlier this week') return d.toLocaleDateString(undefined, { weekday: 'long' });
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' });
  }
  function both(it) { return (it.product === 'curated' && it.kind === 'matched') || (it.product === 'courted' && it.kind === 'date_matched') || (it.product === 'catered' && it.kind === 'recipe_matched'); }
  function sentenceR(it, name) {
    var n = it.n || 1, s = it.subjects || [];
    if (it.product === 'curated' && it.kind === 'matched') return '<b>You and ' + name + '</b> both want to watch ' + (n > 1 ? n + ' titles: ' + list(s, n) : '<b>' + esc(s[0] || '') + '</b>') + '.';
    if (it.product === 'courted' && it.kind === 'date_matched') return '<b>You and ' + name + '</b> both said yes to ' + (n > 1 ? n + ' places' : 'a place') + '. ' + (n > 1 ? 'They’re' : 'It’s') + ' in your date ideas.';
    // Explore › Cook (0057): the match saved it to your recipes
    if (it.product === 'catered' && it.kind === 'recipe_matched') return '<b>You and ' + name + '</b> both want to cook ' + (n > 1 ? n + ' recipes: ' + list(s, n) : '<b>' + esc(s[0] || '') + '</b>') + '.';
    var t = sentence(it);
    return (it.actor ? '<b>' + name + '</b> ' : '') + t + (/[.?!]$/.test(t.replace(/<[^>]+>/g, '')) ? '' : '.');
  }
  function action(it) {
    var k = it.product + ':' + it.kind;
    if (k === 'curated:said_yes') return { t: 'Swipe', h: '/curated/?tab=deck' };
    if (k === 'curated:matched' && (it.n || 1) === 1) return { t: 'Pick a night', h: link(it) };
    if (k === 'curated:new_season') return { t: 'Add to Up next', h: '/curated/?tab=list' };
    if (k === 'catered:recipe_saved' || k === 'catered:recipe_matched') return { t: (it.n || 1) > 1 ? 'Plan one' : 'Plan it', h: '/catered/?tab=week' };
    if (k === 'courted:date_proposed') return { t: 'Answer', h: '/courted/' };
    if (k === 'courted:date_matched') return { t: 'Plan one', h: '/courted/ideas' };
    if (k === 'week:would_done') return { t: 'Your turn', h: '/?game=would' };
    if (k === 'week:evening_built') return { t: 'Build yours', h: '/?game=evening' };
    if (k === 'week:evening_both') return { t: 'See them', h: '/?game=evening' };
    if (k === 'week:perfect_added' || (k === 'week:evening_planned' && it.ref && it.ref.perfect)) return { t: 'See it', h: link(it) };
    return null;
  }
  function paintPanelR(b) {
    var d = state.data, items = (d && d.items) || [], name = esc(partnerName());
    var page = inShell() || window.matchMedia('(max-width: 620px)').matches;
    var head = page
      ? '<div class="dv-nt-top"><button type="button" class="dv-nt-back" data-nt-close aria-label="Back"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 5.5 8.5 12 15 18.5" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button></div><h2 class="dv-nt-h">Notifications</h2>'
      : '<div class="dv-nt-head"><h2>Notifications</h2>' + (d && d.unread ? '<span>' + d.unread + ' new</span>' : '') + '</div>';
    var foot = inShell() ? '<a class="dv-nt-settings" href="/account/">Notification settings<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 5.5 15.5 12 9 18.5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></a>' : '';
    if (!d) { b.panel.innerHTML = head + '<p class="dv-nt-empty">Loading…</p>'; return; }
    if (!items.length) {
      b.panel.innerHTML = head + '<p class="dv-nt-empty">Nothing new. When ' + name + ' plans something, says yes to a show or adds a gift idea, it’ll show up here.</p>' + foot;
      return;
    }
    var groups = [], at = {};
    items.forEach(function (it) { var g = groupOf(it.at); if (!at[g]) { at[g] = []; groups.push(g); } at[g].push(it); });
    b.panel.innerHTML = head + groups.map(function (g) {
      return '<section class="dv-nt-group"><h3>' + g + '</h3><div class="dv-nt-list">' + at[g].map(function (it) {
        var p = PRODUCT[it.product] || { label: it.product, tint: 'milk' };
        var badge = PATH[it.product] ? '<span class="dv-nt-badge ' + p.tint + '"><svg viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' + PATH[it.product] + '</svg></span>' : '';
        var orbs = both(it)
          ? '<span class="dv-nt-orbs two" aria-hidden="true"><i class="a">Y</i><i class="b">' + esc(name.charAt(0)) + '</i>' + badge + '</span>'
          : '<span class="dv-nt-orbs" aria-hidden="true"><i class="b">' + esc(name.charAt(0)) + '</i>' + badge + '</span>';
        var thumb = it.ref && it.ref.poster ? '<img class="dv-nt-poster" alt="" loading="lazy" src="https://image.tmdb.org/t/p/w92' + esc(it.ref.poster) + '">'
          : it.ref && it.ref.image && /^https:\/\//.test(it.ref.image) ? '<img class="dv-nt-poster" alt="" loading="lazy" referrerpolicy="no-referrer" src="' + esc(it.ref.image) + '">' : '';
        var act = action(it);
        return '<div class="dv-nt-row' + (it.unread ? ' unread' : '') + '"><a class="dv-nt-main" href="' + esc(link(it)) + '">' + orbs +
          '<span class="dv-nt-text"><span>' + sentenceR(it, name) + '</span><small>' + esc(whenR(it.at, g)) + '</small></span>' + thumb + '</a>' +
          (act ? '<a class="dv-nt-act" href="' + esc(act.h) + '">' + act.t + '</a>' : '') + '</div>';
      }).join('') + '</div></section>';
    }).join('') + foot;
  }

  function draw() { bells = bells.filter(function (b) { return b.el.isConnected; }); bells.forEach(function (b) { paintCount(b); if (b.el.classList.contains(OPEN)) paintPanel(b); }); }

  function build() {
    var el = document.createElement('div');
    el.className = 'dv-bell';
    el.innerHTML = '<button type="button" class="dv-bell-btn" aria-haspopup="true" aria-expanded="false" aria-label="Notifications">' +
      ICON_BELL + '<span class="dv-bell-count" style="display:none"></span></button><div class="dv-bell-panel" role="dialog" aria-label="What’s new"></div>';
    var b = { el: el, btn: el.firstChild, count: el.querySelector('.dv-bell-count'), panel: el.querySelector('.dv-bell-panel') };

    function onDoc(e) { if (!el.contains(e.target) && !b.panel.contains(e.target)) close(); }
    function onKey(e) { if (e.key === 'Escape') { close(); b.btn.focus(); } }
    // The panel is fixed to the screen (a toolbar that crops its contents can't hide it):
    // under whichever bar holds the bell, its right edge a little past the bell's.
    // Phones span the width instead (notify.css).
    function place() {
      if (rd()) {
        // The redesign: the panel lives on <body> (a glass header would hold a fixed child); a card under the bell
        // on a computer, the whole screen on a phone and in the app (notify.css)
        if (b.panel.parentNode !== document.body) document.body.appendChild(b.panel);
        b.panel.classList.add('dv-nt');
        var rr = b.btn.getBoundingClientRect();
        b.panel.style.top = Math.round(rr.bottom + 10) + 'px';
        b.panel.style.right = Math.max(16, Math.round(window.innerWidth - rr.right - 12)) + 'px';
        return;
      }
      var bar = el.closest('.snav, .dv-toolbar, nav, header') || el.parentElement;
      var r = b.btn.getBoundingClientRect();
      b.panel.style.top = Math.round(bar.getBoundingClientRect().bottom + 8) + 'px';
      b.panel.style.right = Math.max(12, Math.round(window.innerWidth - r.right - 44)) + 'px';
    }
    function open() {
      place();
      el.classList.add(OPEN); b.panel.classList.add('on'); b.btn.setAttribute('aria-expanded', 'true');
      paintPanel(b);
      if (rd()) { var f = b.panel.querySelector('.dv-nt-back'); if (f) f.focus(); }
      // Mark seen first, then fetch, so a slow mark can't bring the badge back
      markSeen().then(function () { load(true); });
      document.addEventListener('mousedown', onDoc, true); document.addEventListener('keydown', onKey, true);
      window.addEventListener('resize', place);
    }
    function close() {
      if (!el.classList.contains(OPEN)) return;
      el.classList.remove(OPEN); b.panel.classList.remove('on'); b.btn.setAttribute('aria-expanded', 'false');
      document.removeEventListener('mousedown', onDoc, true); document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', place);
      draw();
    }
    b.btn.addEventListener('click', function (e) { e.stopPropagation(); el.classList.contains(OPEN) ? close() : open(); });
    // Marked seen when the panel opened; once it's read and clicked, it's done.
    b.panel.addEventListener('click', function (e) { if (e.target.closest('a, [data-nt-close]')) close(); });
    return b;
  }

  /* Put a bell just before an account avatar. Idempotent. */
  function attach(acct) {
    if (!acct || !acct.parentNode) return;
    var prev = acct.previousElementSibling;
    if (prev && prev.classList && prev.classList.contains('dv-bell')) return;
    var b = build();
    acct.parentNode.insertBefore(b.el, acct);
    bells.push(b);
    paintCount(b);
  }

  function boot() {
    var c = readCache();
    if (c && c.id === myId()) state.data = c.data;
    document.querySelectorAll('.dv-acct').forEach(attach);
    load(true);
    setInterval(function () { if (!document.hidden) load(); }, POLL_MS);
    document.addEventListener('visibilitychange', function () { if (!document.hidden) load(); });
    // A product page that writes something (or gets a fresh token) can nudge us.
    window.addEventListener('dv:activity', function () { setTimeout(function () { load(true); }, 1500); });
  }

  /* Started by the account menu once someone is signed in, never on its own:
     signed-out pages make no requests. */
  var booted = false;
  window.DVNotify = {
    attach: function (el) {
      if (!booted) { booted = true; boot(); }   // boot attaches to every .dv-acct
      attach(el);                              // idempotent
    },
    refresh: function () { load(true); },
  };
})();

export function attachBell(el) { window.DVNotify.attach(el); }
